import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Autorenew as AutorenewIcon,
  PlayArrow as PlayArrowIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { todayDateInputValue } from '../../utils/datetimeLocal';
import CreateFromUnbilledDialog from '../../components/Billing/CreateFromUnbilledDialog';
import ClientAutocomplete from '../../components/Common/ClientAutocomplete';
import CaseAutocomplete from '../../components/Common/CaseAutocomplete';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import ListPagination from '../../components/Common/ListPagination';
import { useAuth } from '../../contexts/AuthContext';
import { userHasPermission } from '../../utils/permissions';
import { getAuthToken } from '../../lib/authToken';

interface Invoice {
  id: string;
  invoiceNumber: string;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  case?: {
    id: string;
    title: string;
    caseNumber: string;
  };
  status: string;
  type: string;
  issueDate: string;
  dueDate: string;
  totalAmount: number;
  balanceDue: number;
  paidAmount: number;
  currency: string;
  notes?: string;
  items?: any[] | string;
  billingInfo?: any;
  payments?: Array<{
    id?: string;
    date?: string;
    amount?: number;
    method?: string;
    reference?: string;
    notes?: string;
    source?: string;
  }>;
}

interface RecurringSchedule {
  id: string;
  title: string;
  clientId: string;
  client?: { id: string; firstName: string; lastName: string };
  frequency: string;
  amount: number;
  nextRunAt: string;
  isActive?: boolean;
}

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const InvoicesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canWriteBilling = userHasPermission(user, 'billing:write');
  const canDeleteBilling = userHasPermission(user, 'billing:delete');
  const [pageTab, setPageTab] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: '',
  });
  useEffect(() => {
    setPage(1);
  }, [filters]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'check',
    reference: '',
    notes: '',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [unbilledDialogOpen, setUnbilledDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    clientId: '',
    caseId: '',
    type: 'hourly',
    subtotal: 0,
    notes: '',
  });
  const [editForm, setEditForm] = useState({
    id: '',
    clientId: '',
    caseId: '',
    type: 'hourly',
    subtotal: 0,
    notes: '',
    status: 'draft',
    items: [],
    billingInfo: {},
  });

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<RecurringSchedule | null>(null);
  const [scheduleForm, setScheduleForm] = useState({
    title: '',
    clientId: '',
    frequency: 'monthly',
    amount: 0,
    nextRunAt: format(new Date(), 'yyyy-MM-dd'),
  });
  const [deleteScheduleTarget, setDeleteScheduleTarget] = useState<RecurringSchedule | null>(null);
  const [processingRecurring, setProcessingRecurring] = useState(false);

  // Fetch invoices
  const { data: invoicesData, isLoading, error: invoicesError, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices', user?.tenantId, filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      params.set('page', String(page));
      params.set('limit', String(limit));

      const response = await fetch(`/api/invoices?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }
      return response.json();
    },
  });

  // Fetch invoice statistics
  const { data: statsData } = useQuery({
    queryKey: ['invoice-stats', user?.tenantId],
    queryFn: async () => {
      const response = await fetch('/api/invoices/stats/summary', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch invoice statistics');
      }
      return response.json();
    },
  });

  const { data: tenantPaymentsData } = useQuery({
    queryKey: ['invoice-payments', user?.tenantId],
    queryFn: async () => {
      const response = await fetch('/api/invoice-payments', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Unable to load payments'));
      return response.json();
    },
    enabled: pageTab === 3 && Boolean(user?.tenantId),
  });

  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: ['recurring-schedules', user?.tenantId],
    queryFn: async () => {
      const response = await fetch('/api/billing/recurring-schedules', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to load recurring schedules.'));
      }
      return response.json();
    },
  });

  const { data: arAgingData } = useQuery({
    queryKey: ['ar-aging', user?.tenantId],
    queryFn: async () => {
      const response = await fetch('/api/reports/ar-aging', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!response.ok) {
        // Non-billing roles may be denied — fail soft
        if (response.status === 403) return null;
        throw new Error(await getApiErrorMessage(response, 'Unable to load AR aging.'));
      }
      return response.json();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ invoiceId, status }: { invoiceId: string; status: string }) => {
      const response = await fetch(`/api/invoices/${invoiceId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update invoice status'));
      }
      return response.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['ar-aging'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      toast.success(
        vars.status === 'paid'
          ? 'Invoice marked paid — balance cleared'
          : 'Invoice status updated successfully'
      );
    },
    onError: (error) => {
      toast.error(toUserErrorMessage(error, 'Failed to update invoice status'));
      console.error('Update error:', error);
    },
  });

  const sendInvoiceEmailMutation = useMutation({
    mutationFn: async (invoice: Invoice) => {
      const to = invoice.client?.email;
      if (!to) {
        throw new Error('Client has no email address on file');
      }
      const response = await fetch(`/api/billing/${invoice.id}/email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailData: {
            to,
            subject: `Invoice ${invoice.invoiceNumber}`,
            message: `Please find attached invoice ${invoice.invoiceNumber} for $${Number(invoice.totalAmount || 0).toFixed(2)}.`,
          },
        }),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to email invoice'));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['ar-aging'] });
      toast.success('Invoice emailed to client');
    },
    onError: (error) => {
      toast.error(toUserErrorMessage(error, 'Failed to email invoice'));
    },
  });

  const handleSendInvoice = (invoice: Invoice) => {
    const to = invoice.client?.email;
    if (!to) {
      toast.error('Client has no email address on file');
      return;
    }
    if (
      window.confirm(
        `Email invoice ${invoice.invoiceNumber} to ${to}? A PDF will be attached when available.`
      )
    ) {
      sendInvoiceEmailMutation.mutate(invoice);
    }
  };

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete invoice');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete invoice');
      console.error('Delete error:', error);
    },
  });

  const handleStatusChange = (invoiceId: string, newStatus: string) => {
    updateStatusMutation.mutate({ invoiceId, status: newStatus });
  };

  const applyPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoice) throw new Error('No invoice selected');
      const response = await fetch(`/api/invoices/${selectedInvoice.id}/payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Number(paymentForm.amount),
          method: paymentForm.method,
          reference: paymentForm.reference,
          notes: paymentForm.notes,
        }),
      });
      if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Unable to apply payment'));
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      toast.success('Payment applied to invoice');
      setPaymentDialogOpen(false);
      setViewDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(toUserErrorMessage(error, 'Unable to apply payment'));
    },
  });

  const handleDeleteInvoice = (invoiceId: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      deleteInvoiceMutation.mutate(invoiceId);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
  };

  const handleViewArInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to load invoice'));
      }
      const json = await response.json();
      const invoice = (json.data?.invoice || json.data) as Invoice;
      setSelectedInvoice(invoice);
      setViewDialogOpen(true);
    } catch (err: any) {
      toast.error(toUserErrorMessage(err, 'Unable to load invoice'));
    }
  };

  const handleCreateInvoice = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create invoice');
      }
      
      toast.success('Invoice created successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setCreateDialogOpen(false);
      setCreateForm({
        clientId: '',
        caseId: '',
        type: 'hourly',
        subtotal: 0,
        notes: '',
      });
    } catch (error) {
      toast.error('Failed to create invoice');
      console.error('Create invoice error:', error);
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    // Parse items if it's a string, otherwise use as is
    let items = [];
    if (invoice.items) {
      if (typeof invoice.items === 'string') {
        try {
          items = JSON.parse(invoice.items);
        } catch (e) {
          console.error('Failed to parse items:', e);
          items = [];
        }
      } else if (Array.isArray(invoice.items)) {
        items = invoice.items;
      }
    }

    setEditForm({
      id: invoice.id,
      clientId: invoice.client.id,
      caseId: invoice.case?.id || '',
      type: invoice.type,
      subtotal: invoice.totalAmount,
      notes: invoice.notes || '',
      status: invoice.status,
      items: items,
      billingInfo: invoice.billingInfo || {},
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const totalAmount = (editForm.items || []).reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
      
      const response = await fetch(`/api/invoices/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editForm,
          subtotal: totalAmount,
          totalAmount: totalAmount,
          items: editForm.items || [],
        }),
      });
      
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update invoice'));
      }
      
      toast.success('Invoice updated successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setEditDialogOpen(false);
      setEditForm({
        id: '',
        clientId: '',
        caseId: '',
        type: 'hourly',
        subtotal: 0,
        notes: '',
        status: 'draft',
        items: [],
        billingInfo: {},
      });
    } catch (error) {
      toast.error(toUserErrorMessage(error, 'Failed to update invoice'));
      console.error('Update invoice error:', error);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      const response = await fetch(`/api/invoices/${invoice.id}/download`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
      console.error('Download invoice error:', error);
    }
  };

  const handlePreviewInvoice = async () => {
    try {
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      let previewClient: any = null;
      let previewCase: any = null;
      if (editForm.clientId) {
        const cres = await fetch(`/api/clients/${editForm.clientId}`, { headers });
        if (cres.ok) {
          const cjson = await cres.json();
          previewClient = cjson.data?.client || cjson.data;
        }
      }
      if (editForm.caseId) {
        const kres = await fetch(`/api/cases/${editForm.caseId}`, { headers });
        if (kres.ok) {
          const kjson = await kres.json();
          previewCase = kjson.data?.case || kjson.data;
        }
      }

      const previewInvoice = {
        ...editForm,
        invoiceNumber: editForm.id ? `PREVIEW-${editForm.id.substring(0, 8)}` : 'PREVIEW-001',
        issueDate: new Date().toISOString(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        totalAmount: (editForm.items || []).reduce((sum: number, item: any) => sum + (item.amount || 0), 0),
        balanceDue: (editForm.items || []).reduce((sum: number, item: any) => sum + (item.amount || 0), 0),
        currency: 'USD',
        client: previewClient,
        case: previewCase,
      };

      const response = await fetch('/api/invoices/preview', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(previewInvoice),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Received empty PDF file');
      }
      
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      toast.success('Preview generated successfully');
    } catch (error: any) {
      console.error('Preview error:', error);
      toast.error(`Failed to generate preview: ${error.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'sent': return 'primary';
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'cancelled': return 'error';
      case 'refunded': return 'warning';
      case 'partially_paid': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent';
      case 'paid': return 'Paid';
      case 'overdue': return 'Overdue';
      case 'cancelled': return 'Cancelled';
      case 'refunded': return 'Refunded';
      case 'partially_paid': return 'Partially Paid';
      default: return status;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const schedules: RecurringSchedule[] =
    schedulesData?.data?.schedules ?? schedulesData?.data ?? [];

  const resetScheduleForm = () => {
    setScheduleForm({
      title: '',
      clientId: '',
      frequency: 'monthly',
      amount: 0,
      nextRunAt: format(new Date(), 'yyyy-MM-dd'),
    });
    setEditingSchedule(null);
  };

  const openCreateSchedule = () => {
    resetScheduleForm();
    setScheduleDialogOpen(true);
  };

  const openEditSchedule = (schedule: RecurringSchedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      title: schedule.title,
      clientId: schedule.clientId,
      frequency: schedule.frequency,
      amount: schedule.amount,
      nextRunAt: String(schedule.nextRunAt).slice(0, 10),
    });
    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const url = editingSchedule
        ? `/api/billing/recurring-schedules/${editingSchedule.id}`
        : '/api/billing/recurring-schedules';
      const response = await fetch(url, {
        method: editingSchedule ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scheduleForm),
      });
      if (!response.ok) {
        throw new Error(
          await getApiErrorMessage(
            response,
            editingSchedule ? 'Unable to update schedule.' : 'Unable to create schedule.'
          )
        );
      }
      toast.success(editingSchedule ? 'Schedule updated' : 'Schedule created');
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] });
      setScheduleDialogOpen(false);
      resetScheduleForm();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Unable to save schedule.'));
    }
  };

  const handleDeleteSchedule = async () => {
    if (!deleteScheduleTarget) return;
    try {
      const response = await fetch(
        `/api/billing/recurring-schedules/${deleteScheduleTarget.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to delete schedule.'));
      }
      toast.success('Schedule deleted');
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] });
      setDeleteScheduleTarget(null);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Unable to delete schedule.'));
    }
  };

  const handleProcessRecurring = async () => {
    setProcessingRecurring(true);
    try {
      const response = await fetch('/api/billing/process-recurring', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to process recurring schedules.'));
      }
      const json = await response.json();
      const processed = json.data?.processed ?? json.data?.count ?? 0;
      toast.success(`Processed ${processed} due schedule(s)`);
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Unable to process recurring schedules.'));
    } finally {
      setProcessingRecurring(false);
    }
  };

  const invoices = invoicesData?.data?.invoices || [];
  const invoicesTotal = invoicesData?.data?.pagination?.total || invoices.length;
  const stats = statsData?.data;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Invoices
      </Typography>
      {invoicesError && (
        <ListLoadErrorAlert
          resourceLabel="invoices"
          onRetry={() => refetchInvoices()}
        />
      )}

      <Tabs value={pageTab} onChange={(_, v) => setPageTab(v)} sx={{ mb: 3 }}>
        <Tab label="All invoices" />
        <Tab label="Recurring schedules" icon={<AutorenewIcon fontSize="small" />} iconPosition="start" />
        <Tab label="AR aging" />
        <Tab label="Payments" icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" />
      </Tabs>

      {pageTab === 2 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Accounts receivable aging
          </Typography>
          {!arAgingData ? (
            <Alert severity="info">AR aging is available to billing roles.</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total AR: {formatCurrency(arAgingData.data?.totalAR || 0)}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {Object.entries(arAgingData.data?.buckets || {}).map(([key, bucket]: [string, any]) => (
                  <Grid item xs={12} sm={6} md={2} key={key}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          {bucket.label}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(bucket.amount || 0)}
                        </Typography>
                        <Typography variant="body2">{bucket.count} invoice(s)</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell>Bucket</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(arAgingData.data?.buckets || {}).flatMap(
                      ([key, bucket]: [string, any]) =>
                        (bucket.invoices || []).map((inv: any) => (
                          <TableRow
                            key={`${key}-${inv.id}`}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleViewArInvoice(inv.id)}
                          >
                            <TableCell>{inv.invoiceNumber}</TableCell>
                            <TableCell>{inv.clientName}</TableCell>
                            <TableCell>
                              {inv.dueDate
                                ? String(inv.dueDate).slice(0, 10)
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={bucket.label} />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(inv.balanceDue)}
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                title="View"
                                onClick={() => handleViewArInvoice(inv.id)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {inv.status !== 'paid' && (
                                <Button
                                  size="small"
                                  onClick={() => handleStatusChange(inv.id, 'paid')}
                                >
                                  Mark paid
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {pageTab === 3 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payment applications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Online portal payments and staff applications against operating invoices. These do not post to trust/IOLTA.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tenantPaymentsData?.data || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No payments recorded yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (tenantPaymentsData.data as any[]).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {p.appliedAt || p.createdAt
                          ? format(new Date(p.appliedAt || p.createdAt), 'MMM d, yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {p.client
                          ? `${p.client.firstName || ''} ${p.client.lastName || ''}`.trim()
                          : '—'}
                      </TableCell>
                      <TableCell>{p.invoice?.invoiceNumber || '—'}</TableCell>
                      <TableCell>{p.source}</TableCell>
                      <TableCell>{String(p.method || '').replace(/_/g, ' ')}</TableCell>
                      <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.status} color={p.status === 'succeeded' ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {pageTab === 1 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">Recurring schedules</Typography>
              <Typography variant="body2" color="text.secondary">
                Automatically generate invoices on a fixed cadence.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canWriteBilling && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={processingRecurring ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                    onClick={handleProcessRecurring}
                    disabled={processingRecurring}
                  >
                    Run due schedules now
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateSchedule}>
                    Add schedule
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {schedulesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : schedules.length === 0 ? (
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <AutorenewIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No recurring schedules
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set up monthly retainers or other repeating invoices.
                </Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateSchedule} disabled={!canWriteBilling}>
                  Add schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Next run</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id} hover>
                      <TableCell>{schedule.title}</TableCell>
                      <TableCell>
                        {schedule.client
                          ? `${schedule.client.firstName} ${schedule.client.lastName}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {FREQUENCY_OPTIONS.find((f) => f.value === schedule.frequency)?.label ||
                          schedule.frequency}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(schedule.amount)}</TableCell>
                      <TableCell>{formatDate(schedule.nextRunAt)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={schedule.isActive === false ? 'Paused' : 'Active'}
                          color={schedule.isActive === false ? 'default' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWriteBilling && (
                          <IconButton size="small" onClick={() => openEditSchedule(schedule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canDeleteBilling && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteScheduleTarget(schedule)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {pageTab === 0 && (
        <>
      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Invoices
                </Typography>
                <Typography variant="h4">
                  {stats.totalInvoices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(stats.totalAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Outstanding
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(stats.totalOutstanding)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Overdue
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.overdueInvoices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Search by invoice number or notes..."
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="retainer">Retainer</MenuItem>
                <MenuItem value="contingency">Contingency</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            {canWriteBilling && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
                onClick={handleCreateInvoice}
              >
                New Invoice
              </Button>
            )}
          </Grid>
          <Grid item xs={12} sm={2}>
            {canWriteBilling && (
              <Button
                variant="outlined"
                startIcon={<ReceiptLongIcon />}
                fullWidth
                onClick={() => setUnbilledDialogOpen(true)}
              >
                From unbilled
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Invoices Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Case</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="right">Balance Due</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading invoices...
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice: Invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {invoice.invoiceNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.client.firstName} {invoice.client.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {invoice.client.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {invoice.case ? (
                        <Typography variant="body2">
                          {invoice.case.title}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No case
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(invoice.status)}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(invoice.issueDate)}
                    </TableCell>
                    <TableCell>
                      {formatDate(invoice.dueDate)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(invoice.totalAmount, invoice.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={invoice.balanceDue > 0 ? 'error' : 'success'}
                        fontWeight="bold"
                      >
                        {formatCurrency(invoice.balanceDue, invoice.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewInvoice(invoice)}
                        title="View Invoice"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {canWriteBilling && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditInvoice(invoice)}
                          title="Edit Invoice"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadInvoice(invoice)}
                        title="Download PDF"
                      >
                        <DownloadIcon />
                      </IconButton>
                      {canWriteBilling && (
                        <IconButton
                          size="small"
                          onClick={() => handleSendInvoice(invoice)}
                          title={
                            invoice.client?.email
                              ? `Email invoice to ${invoice.client.email}`
                              : 'Send Invoice (client email required)'
                          }
                          disabled={
                            invoice.status === 'paid'
                            || sendInvoiceEmailMutation.isPending
                            || !invoice.client?.email
                          }
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                      {canDeleteBilling && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteInvoice(invoice.id)}
                          title="Delete Invoice"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <ListPagination
          page={page}
          limit={limit}
          total={invoicesTotal}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </Paper>
        </>
      )}

      {/* Recurring schedule dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => {
          setScheduleDialogOpen(false);
          resetScheduleForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingSchedule ? 'Edit schedule' : 'Add recurring schedule'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={scheduleForm.title}
              onChange={(e) => setScheduleForm((p) => ({ ...p, title: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <ClientAutocomplete
              label="Client"
              value={scheduleForm.clientId || null}
              onChange={(c) => setScheduleForm((p) => ({ ...p, clientId: c?.id || '' }))}
              textFieldProps={{ sx: { mb: 2 } }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={scheduleForm.frequency}
                label="Frequency"
                onChange={(e) => setScheduleForm((p) => ({ ...p, frequency: e.target.value }))}
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={scheduleForm.amount}
              onChange={(e) =>
                setScheduleForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))
              }
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              fullWidth
              label="Next run date"
              type="date"
              value={scheduleForm.nextRunAt}
              onChange={(e) => setScheduleForm((p) => ({ ...p, nextRunAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setScheduleDialogOpen(false);
              resetScheduleForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSchedule}
            disabled={!scheduleForm.title || !scheduleForm.clientId}
          >
            {editingSchedule ? 'Save changes' : 'Create schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteScheduleTarget)} onClose={() => setDeleteScheduleTarget(null)}>
        <DialogTitle>Delete recurring schedule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This stops future automatic invoices for &ldquo;{deleteScheduleTarget?.title}&rdquo;.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteScheduleTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteSchedule}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Invoice Details
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedInvoice.invoiceNumber}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Client: {selectedInvoice.client.firstName} {selectedInvoice.client.lastName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Status: {getStatusLabel(selectedInvoice.status)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Total Amount: {formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Balance Due: {formatCurrency(selectedInvoice.balanceDue, selectedInvoice.currency)}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Line items
              </Typography>
              {(() => {
                let items: any[] = [];
                if (selectedInvoice.items) {
                  if (typeof selectedInvoice.items === 'string') {
                    try {
                      items = JSON.parse(selectedInvoice.items);
                    } catch {
                      items = [];
                    }
                  } else if (Array.isArray(selectedInvoice.items)) {
                    items = selectedInvoice.items;
                  }
                }
                if (!items.length) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      No line items on this invoice.
                    </Typography>
                  );
                }
                return (
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Qty / Hours</TableCell>
                          <TableCell align="right">Rate</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{item.description || item.name || '—'}</TableCell>
                            <TableCell>{item.type || '—'}</TableCell>
                            <TableCell align="right">
                              {item.quantity ?? item.hours ?? item.qty ?? '—'}
                            </TableCell>
                            <TableCell align="right">
                              {item.rate != null
                                ? formatCurrency(item.rate, selectedInvoice.currency)
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(
                                item.amount ??
                                  (Number(item.rate || 0) *
                                    Number(item.quantity ?? item.hours ?? item.qty ?? 0)),
                                selectedInvoice.currency
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Payment applications
              </Typography>
              {Array.isArray(selectedInvoice.payments) && selectedInvoice.payments.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Reference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice.payments.map((p, idx) => (
                        <TableRow key={p.id || idx}>
                          <TableCell>
                            {p.date ? format(new Date(p.date), 'MMM d, yyyy') : '—'}
                          </TableCell>
                          <TableCell>{p.method || '—'}</TableCell>
                          <TableCell>{p.source || '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(p.amount, selectedInvoice.currency)}</TableCell>
                          <TableCell>{p.reference || p.notes || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No payments applied yet.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canWriteBilling && selectedInvoice && Number(selectedInvoice.balanceDue) > 0 && (
            <Button
              onClick={() => {
                setPaymentForm({
                  amount: String(selectedInvoice.balanceDue || ''),
                  method: 'check',
                  reference: '',
                  notes: '',
                });
                setPaymentDialogOpen(true);
              }}
            >
              Apply payment
            </Button>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply payment to invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Records an operating payment application. Does not post to trust/IOLTA.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm((f) => ({ ...f, amount: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Method</InputLabel>
            <Select
              label="Method"
              value={paymentForm.method}
              onChange={(e) => setPaymentForm((f) => ({ ...f, method: String(e.target.value) }))}
            >
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="credit_card">Credit card (in office)</MenuItem>
              <MenuItem value="bank_transfer">Bank transfer</MenuItem>
              <MenuItem value="stripe">Stripe (manual)</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reference"
            value={paymentForm.reference}
            onChange={(e) => setPaymentForm((f) => ({ ...f, reference: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Notes"
            value={paymentForm.notes}
            onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={applyPaymentMutation.isPending || !(Number(paymentForm.amount) > 0)}
            onClick={() => applyPaymentMutation.mutate()}
          >
            {applyPaymentMutation.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Create New Invoice
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <ClientAutocomplete
              label="Client"
              required
              value={createForm.clientId || null}
              onChange={(c) =>
                setCreateForm((prev) => ({
                  ...prev,
                  clientId: c?.id || '',
                  caseId: '',
                }))
              }
              textFieldProps={{ sx: { mb: 2 } }}
            />

            <CaseAutocomplete
              label="Case (Optional)"
              value={createForm.caseId || null}
              onChange={(c) =>
                setCreateForm((prev) => ({
                  ...prev,
                  caseId: c?.id || '',
                  ...(c?.clientId ? { clientId: c.clientId } : {}),
                }))
              }
              extraParams={createForm.clientId ? { clientId: createForm.clientId } : undefined}
              textFieldProps={{ sx: { mb: 2 } }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={createForm.type}
                onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value }))}
                label="Type"
              >
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="retainer">Retainer</MenuItem>
                <MenuItem value="contingency">Contingency</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Subtotal"
              type="number"
              value={createForm.subtotal}
              onChange={(e) => setCreateForm(prev => ({ ...prev, subtotal: parseFloat(e.target.value) || 0 }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={createForm.notes}
              onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateSubmit}
            disabled={!createForm.clientId}
          >
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Edit Invoice - {editForm.id ? `Invoice ${editForm.id.substring(0, 8)}` : 'New Invoice'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="sent">Sent</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="refunded">Refunded</MenuItem>
                    <MenuItem value="partially_paid">Partially Paid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editForm.type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                    label="Type"
                  >
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="fixed">Fixed</MenuItem>
                    <MenuItem value="retainer">Retainer</MenuItem>
                    <MenuItem value="contingency">Contingency</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ClientAutocomplete
                  label="Client"
                  required
                  value={editForm.clientId || null}
                  onChange={(c) =>
                    setEditForm((prev) => ({
                      ...prev,
                      clientId: c?.id || '',
                      caseId: '',
                    }))
                  }
                  textFieldProps={{ sx: { mb: 2 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CaseAutocomplete
                  label="Case (Optional)"
                  value={editForm.caseId || null}
                  onChange={(c) =>
                    setEditForm((prev) => ({
                      ...prev,
                      caseId: c?.id || '',
                      ...(c?.clientId ? { clientId: c.clientId } : {}),
                    }))
                  }
                  extraParams={editForm.clientId ? { clientId: editForm.clientId } : undefined}
                  textFieldProps={{ sx: { mb: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>

            {/* Line Items */}
            <Typography variant="h6" gutterBottom>
              Line Items
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                startIcon={<AddCircleIcon />}
                onClick={() => setEditForm(prev => ({
                  ...prev,
                  items: [...(prev.items || []), {
                    id: Date.now().toString(),
                    type: 'service',
                    description: '',
                    quantity: 1,
                    rate: 0,
                    amount: 0,
                    date: todayDateInputValue(),
                    timekeeper: ''
                  }]
                }))}
              >
                Add Item
              </Button>
            </Box>

            <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 3 }}>
              {(editForm.items || []).map((item: any, index: number) => (
                <Paper key={item.id || `item-${index}`} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={item.type}
                          onChange={(e) => {
                            const newItems = [...(editForm.items || [])];
                            newItems[index].type = e.target.value;
                            setEditForm(prev => ({ ...prev, items: newItems }));
                          }}
                          label="Type"
                        >
                          <MenuItem value="service">Service</MenuItem>
                          <MenuItem value="expense">Expense</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={item.description}
                        onChange={(e) => {
                          const newItems = [...(editForm.items || [])];
                          newItems[index].description = e.target.value;
                          setEditForm(prev => ({ ...prev, items: newItems }));
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Quantity"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...(editForm.items || [])];
                          newItems[index].quantity = parseFloat(e.target.value) || 0;
                          newItems[index].amount = newItems[index].quantity * newItems[index].rate;
                          setEditForm(prev => ({ ...prev, items: newItems }));
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Rate"
                        type="number"
                        value={item.rate}
                        onChange={(e) => {
                          const newItems = [...(editForm.items || [])];
                          newItems[index].rate = parseFloat(e.target.value) || 0;
                          newItems[index].amount = newItems[index].quantity * newItems[index].rate;
                          setEditForm(prev => ({ ...prev, items: newItems }));
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Amount"
                        type="number"
                        value={item.amount}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const newItems = (editForm.items || []).filter((_, i) => i !== index);
                          setEditForm(prev => ({ ...prev, items: newItems }));
                        }}
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>

            {/* Summary */}
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography>
                    Total Services: ${(editForm.items || []).filter((item: any) => item.type === 'service').reduce((sum: number, item: any) => sum + (item.amount || 0), 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography>
                    Total Expenses: ${(editForm.items || []).filter((item: any) => item.type === 'expense').reduce((sum: number, item: any) => sum + (item.amount || 0), 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="h6">
                    Total: ${(editForm.items || []).reduce((sum: number, item: any) => sum + (item.amount || 0), 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="outlined"
            onClick={handlePreviewInvoice}
            disabled={!editForm.clientId}
          >
            Preview PDF
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSubmit}
            disabled={!editForm.clientId}
          >
            Update Invoice
          </Button>
        </DialogActions>
      </Dialog>

      <CreateFromUnbilledDialog
        open={unbilledDialogOpen}
        onClose={() => setUnbilledDialogOpen(false)}
      />
    </Box>
  );
};

export default InvoicesPage; 